/**
 * The town's props (v1 ticket 40): the signpost with its lantern, and the founding stone once it is laid. They are
 * not buildings: no material, no box, not in the counts, the legend, tending or the round's bricks. Both looks draw
 * them where the layout puts them (`Layout.props`: the middle of the empty land, else the oldest district's
 * corner), and a DOM label carries the town's name on the signpost and the year on the stone (props-label.ts).
 * No DOM here: the unit tests run this under Node.
 */

/** What the props show: the town's name (absent until named), the founding night and whether the stone is laid. */
export interface TownProps {
  /** The name as the learner gave it; absent until named ("Your town" on the signpost then). */
  name?: string;
  /** The night the town was founded (ISO): the stone's year. Absent when unknown. */
  foundedAt?: string;
  /** The first stone is laid: it stands by the signpost, carved with the year (and the name, if any). */
  stone: boolean;
}

/**
 * The props of the learner's own town, from `GET /api/town`'s `name`, `foundedAt` and `stone` (own-town.ts). Any of
 * them may be missing (a server from before ticket 40, a town never named): the signpost always stands, the stone
 * only once laid, and a name that is not a non-empty string counts as none.
 */
export function townPropsOf(t: object): TownProps {
  const o = t as { name?: unknown; foundedAt?: unknown; stone?: unknown };
  const name = typeof o.name === 'string' ? o.name.trim() : '';
  const at = typeof o.foundedAt === 'string' && !Number.isNaN(Date.parse(o.foundedAt)) ? o.foundedAt : '';
  return { ...(name ? { name } : {}), ...(at ? { foundedAt: at } : {}), stone: o.stone === true };
}

/**
 * The stone's year in the interface's calendar, in Latin digits (the label writes them with `num()`): the Persian
 * interface counts years as its dates do (1405), the English one as 2026. Null without a founding night.
 */
export function stoneYear(foundedAt: string | undefined, lang: string): string | null {
  const d = foundedAt ? new Date(foundedAt) : null;
  if (!d || Number.isNaN(d.getTime())) return null;
  try {
    const parts = new Intl.DateTimeFormat(`${lang}-u-nu-latn`, { year: 'numeric' }).formatToParts(d);
    return parts.find((p) => p.type === 'year')?.value ?? String(d.getFullYear());
  } catch {
    return String(d.getFullYear());
  }
}

// ---- where the props stand, and how tall they are (world px, as layout.ts measures) ----------------------------

/** The signpost's board (the DOM label's foot) and the post's top, where the lantern hangs. */
export const BOARD_Z = 14;
export const POST_TOP = 19;
/** The lantern's top: the highest point of the props. */
export const LANTERN_TOP = POST_TOP + 4.1;
/** The founding stone's height. */
export const STONE_Z = 3;

/** Where the props stand, in lot coordinates: the post, the stone beside it, and the islet under them (if any). */
export interface PropsSpot {
  /** The cell they stand in. */
  cell: [number, number];
  /** The post's foot. */
  post: [number, number];
  /** The stone's footprint (x0, y0, x1, y1). */
  stone: [number, number, number, number];
  /**
   * A small raised quay in the water at a district's corner, (x0, y0, x1, y1) at the ground's height, touching the
   * district's land at its corner; null on the empty land, where they stand on the land itself.
   */
  islet: [number, number, number, number] | null;
}

/** The islet's side, in cells. */
const ISLET = 0.64;

/**
 * The props in cell (cx, cy): on the land's middle (`toward` null), or on an islet pushed against the land that
 * lies `toward` (each -1, 0 or 1 along x and y) of it.
 */
export function propsSpot(cx: number, cy: number, toward: [number, number] | null): PropsSpot {
  const at = (c: number, d: number) => (d > 0 ? c + 1 - ISLET : d < 0 ? c : c + (1 - ISLET) / 2);
  const x0 = toward ? at(cx, toward[0]) : cx, y0 = toward ? at(cy, toward[1]) : cy;
  const mx = toward ? x0 + ISLET / 2 : cx + 0.5, my = toward ? y0 + ISLET / 2 : cy + 0.5;
  return {
    cell: [cx, cy],
    post: [mx - 0.1, my + 0.02],
    stone: [mx + 0.02, my - 0.15, mx + 0.22, my - 0.01],
    islet: toward ? [x0, y0, x0 + ISLET, y0 + ISLET] : null,
  };
}
